/**
 * How full a chat's context is, and when it compacts — shared by the server (the auto-compaction
 * trigger) and the client (the context meter) so both agree.
 *
 * MEASURED, not guessed: every answer records the prompt size xAI itself reported for that turn's
 * first request (system prompt + tool schemas + memory + the conversation) plus the answer it adds —
 * i.e. what the next turn starts with — split into those parts (ContextUsage). Only a chat with no
 * measured answer yet falls back to the text estimate below.
 *
 * Compaction = the older messages are summarized into a structured recap; the AI then continues from
 * (summary + the last COMPACT_KEEP_RECENT messages). The user still sees every message.
 */
export const COMPACT_KEEP_RECENT = 12

/** What a chat's next turn starts with, and where it stands against its limit. */
export interface ContextUsage {
  /** Tokens: the last turn's measured prompt + the answer it added. */
  tokens: number
  /** Auto-compaction runs past this. */
  limit: number
  /** The model's whole window. */
  window: number
  /** Where the tokens go (the measured total split by each part's share of the text sent). */
  parts: { system: number; tools: number; memory: number; conversation: number }
  /** false = estimated (after a compaction, or before any measurement). */
  measured: boolean
  /** When it was measured / estimated (epoch ms). */
  at: number
}

/** The user's choice of where long chats compact (Settings → Context); null = the default. */
export type CompactSetting = 200_000 | 300_000 | 400_000 | 'window'
export const DEFAULT_COMPACT_AT: CompactSetting = 300_000

/**
 * Where a chat auto-compacts, in MEASURED prompt tokens. Default 300k — the user's quality-first choice
 * (2026-08-31: compaction is lossy; 2× long-context pricing over 200k accepted). 200k compacts before
 * that price step and deeper into "context rot" territory (Chroma 2025: every frontier model tested
 * degrades as input grows); 'window' only near the model's limit. Never past 90% of the window.
 */
export function compactLimit(model: { context: number; longContextAt: number }, choice: CompactSetting | null = null): number {
  const c = choice ?? DEFAULT_COMPACT_AT
  const cap = Math.round(model.context * 0.9)
  return c === 'window' ? cap : Math.min(c, cap)
}

/** ≈ tokens of text: 3.5 characters a token (Turkish, numbers and markdown tokenize denser than English prose). */
export const approxTokens = (chars: number) => Math.ceil(chars / 3.5)

/**
 * A fallback estimate of the context when nothing is measured yet: the conversation text we'd resend
 * plus the summary, plus the fixed part (system prompt + tool schemas + memory) — ~25k tokens on a
 * typical turn of this app (measured 2026-09-28: instructions ~34k chars, tools 20–100k chars).
 */
export function estimateContextTokens(
  messages: { role: string; content: string; meta?: { quiet?: boolean } }[],
  summary?: string | null,
  fixed = 25_000,
): number {
  let chars = summary?.length ?? 0
  for (const m of messages) {
    if ((m.role === 'user' || m.role === 'assistant') && !m.meta?.quiet) chars += m.content?.length ?? 0
  }
  return approxTokens(chars) + fixed
}

/** "84k" / "1.2M". */
export function tokensText(n: number): string {
  if (n >= 999_500) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1000) return `${Math.round(n / 1000)}k`
  return String(n)
}

/**
 * The context after a turn: `firstInput` = the prompt tokens xAI reported for the turn's first request;
 * `sent` = the characters of each part of that prompt (to split the total); the answer joins the
 * conversation for the next turn.
 */
export function measuredContext(
  firstInput: number,
  sent: { system: number; tools: number; memory: number; conversation: number },
  answerChars: number,
  model: { context: number; longContextAt: number },
  choice: CompactSetting | null = null,
  at = Date.now(),
): ContextUsage {
  const total = sent.system + sent.tools + sent.memory + sent.conversation || 1
  const k = firstInput / total
  const answer = approxTokens(answerChars)
  const system = Math.round(sent.system * k)
  const tools = Math.round(sent.tools * k)
  const memory = Math.round(sent.memory * k)
  // The conversation takes the rest, so the parts add up to the measured total exactly.
  return {
    tokens: firstInput + answer,
    limit: compactLimit(model, choice),
    window: model.context,
    parts: { system, tools, memory, conversation: firstInput - system - tools - memory + answer },
    measured: true,
    at,
  }
}

/** After a compaction: the fixed parts stay, the conversation becomes the summary + the messages kept. */
export function compactedContext(prev: ContextUsage, conversationChars: number, at = Date.now()): ContextUsage {
  const conversation = approxTokens(conversationChars)
  return {
    ...prev,
    tokens: prev.parts.system + prev.parts.tools + prev.parts.memory + conversation,
    parts: { ...prev.parts, conversation },
    measured: false,
    at,
  }
}
